import { Divider, Popconfirm, Table, Tag, Typography, message, Button, Checkbox, Space, Dropdown } from "antd"
import { EyeOutlined } from "@ant-design/icons"
import { useCollection } from "../../../hooks/useCollection"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { CheckCircleOutlined } from "@ant-design/icons"
import { useState } from "react"

//Renames Router Dom "Link" as Direction
import moment from "moment"
import ShowSavedPricesDrawer from "../../../components/Manager/Prices/ShowSavedPricesDrawer"
import PriceDisplay from "../../../components/utils/PriceDisplay"
import NewOrderDrawer from "./NewOrderDrawer"
import RequestNewTechnical from "../../../components/Manager/Prices/RequestNewTechnical"
import useFiltering from "../../../hooks/useFilters"
import OrderHistory from "../../../components/Manager/Prices/OrderHistory"
import TechnicalRequest from "./TechnicalRequests/TechnicalRequest"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import PriceOffer from "../../../components/Manager/PriceOffer/PriceOffer"
import DuplicatePrice from "./DuplicatePrice" // Import the new component
import PriceActions from "./PriceActions"

const { Title, Link, Text } = Typography

export default function SavedPrices() {
  const { user } = useAuthContext()
  const { loading, data } = useCollection(
    "priceCalculation",
    ["user", "==", user.uid],
    ["aproved", "==", true],
  )

  //filters context
  const { selectedFilters, handleTableFilterChange, filterOptions } = useFiltering(
    data,
    "SavedPrices",
  )

  // Column visibility state - Filter Group hidden by default
  const [visibleColumns, setVisibleColumns] = useState({
    clients: true,
    filterGroup: false,
    product: true,
    price: true,
    width: true,
    height: true,
    material: true,
    technical: true,
    order: true,
    actions: true,
    created: true,
  })

  //technical request denial approval
  //deny and send back
  const denyApproval = async (data) => {
    const docref = doc(db, "priceCalculation", data.id)

    try {
      await updateDoc(docref, {
        hasTechincalPage: 0,
      }).then(() => {
        message.success("Message read & saved!")
      })
    } catch (err) {
      console.log(err)
      message.error("Somethin went wrong, check logs")
    }
  }

  //Controls the state of columns
  const columns = [
    {
      title: "Clients",
      dataIndex: "client",
      filters: filterOptions.client,
      onFilter: (value, record) => record.client === value,
      filteredValue: selectedFilters.client || null,
      filterSearch: true,
    },
    {
      title: "Filter Group",
      dataIndex: "filterGroup",
      filters: filterOptions.filterGroup,
      filterSearch: true,
      onFilter: (value, record) => record.filterGroup === value,
      filteredValue: selectedFilters.filterGroup || null,
    },
    {
      title: "Product",
      dataIndex: ["product", "productName"],
      filters: filterOptions.product,
      filterSearch: true,
      onFilter: (value, record) => record.product.productName === value,
      filteredValue: selectedFilters.product || null,
    },
    {
      title: "Price",
      dataIndex: "price",
      filterSearch: true,
      sorter: (a, b) => a.price - b.price,
      render: (value, record) => {
        return <PriceDisplay data={record} />
      },
    },
    {
      title: "Width",
      dataIndex: "labelWidth",
      sorter: (a, b) => a.labelWidth - b.labelWidth,
    },
    {
      title: "Height",
      dataIndex: "labelHeight",
      sorter: (a, b) => a.labelHeight - b.labelHeight,
    },
    {
      title: "Material",
      dataIndex: "material",
      filters: filterOptions.material,
      filterSearch: true,
      onFilter: (value, record) => record.material === value,
      filteredValue: selectedFilters.material || null,
    },
    {
      title: "Technical",
      dataIndex: "hasTechincalPage",
      align: "center",
      render: (value, record) => {
        if (value === 2) {
          return (
            <>
              <Link href={record.technicalPage} target="_blank">
                <Tag color="green">Link</Tag>
              </Link>
              <RequestNewTechnical record={record} />
            </>
          )
        } else if (value === 1) {
          return (
            <Text italic style={{ color: "green" }}>
              progress...
            </Text>
          )
        } else if (value === 3) {
          return (
            <Popconfirm
              title="Comment why the technical was denied"
              description={record.denyComment}
              onConfirm={() => denyApproval(record)}
              okText="Ok"
              cancelText="Cancel">
              <Tag color="red">Deniend</Tag>
            </Popconfirm>
          )
        } else if (record.machine === "Cutter") {
          return <Tag color="blue">Cutter</Tag>
        } else {
          return <TechnicalRequest data={record} />
        }
      },
    },
    {
      title: "Order",
      align: "center",
      render: (value) => (
        <>
          <NewOrderDrawer data={value} />
        </>
      ),
    },
    {
      title: "Actions", // New column
      align: "center",
      render: (value, record) => <PriceActions record={record} />,
    },
    {
      title: "Created",
      dataIndex: ["date", "created"],
      defaultSortOrder: "descend",
      width: "7%",
      sorter: (a, b) => {
        const aSeconds = a?.date?.created?.seconds || a?.timestamp?.seconds || 0
        const bSeconds = b?.date?.created?.seconds || b?.timestamp?.seconds || 0
        return aSeconds - bSeconds
      },
      render: (value, record) => {
        const timestamp = value || record?.date?.created || record?.timestamp
        return (
          <Text>
            {timestamp?.seconds ? moment.unix(timestamp.seconds).format("DD/MM/YYYY") : "N/A"}
          </Text>
        )
      },
    },
  ]

  // Filter columns based on visibility state
  const allColumnsMap = {
    clients: columns[0],
    filterGroup: columns[1],
    product: columns[2],
    price: columns[3],
    width: columns[4],
    height: columns[5],
    material: columns[6],
    technical: columns[7],
    order: columns[8],
    actions: columns[9],
    created: columns[10],
  }

  const visibleColumnsArray = Object.keys(allColumnsMap)
    .filter((key) => visibleColumns[key])
    .map((key) => allColumnsMap[key])

  // Column toggle menu items
  const columnMenuItems = Object.keys(visibleColumns).map((key) => ({
    key,
    label: (
      <Checkbox
        checked={visibleColumns[key]}
        onChange={() => setVisibleColumns({ ...visibleColumns, [key]: !visibleColumns[key] })}>
        {allColumnsMap[key]?.title || key}
      </Checkbox>
    ),
  }))

  return (
    <div style={{ marginTop: "15px" }}>
      <Title level={3} style={{ marginLeft: "18px" }}>
        <CheckCircleOutlined /> <Divider type="vertical" />
        Approved Prices
      </Title>
      <div style={{ marginLeft: "18px", marginBottom: "15px" }}>
        <Dropdown menu={{ items: columnMenuItems }} placement="bottomLeft">
          <Button type="text" style={{ padding: 0 }}>
            Hide/Show Columns ▼
          </Button>
        </Dropdown>
      </div>
      <Table
        key={JSON.stringify(selectedFilters)}
        bordered
        style={{ margin: "15px" }}
        size="small"
        columns={visibleColumnsArray}
        loading={loading}
        dataSource={data && data}
        rowKey={(record) => record.id}
        pagination={{ defaultPageSize: "24", showSizeChanger: true }}
        onChange={handleTableFilterChange}
      />
    </div>
  )
}
